// ─────────────────────────────────────────────────────────────────────────────
// mergedOarlockData.js — Per-stroke merge step.
//
// Runs ALL pure algorithm functions on a single stroke's force/angle arrays,
// then applies the MFW2 doubling rule AT THE BOUNDARY (after the algorithms,
// inside this assembly step). Per spec:
//
//   If MFW2 = true:
//     MXF × 2
//     OPW × 2
//     combinedPower × 2
//
//   CA, FA, EL, SL, WA, MFA are NEVER doubled — they are geometric values.
//
// `combinedPower` is the sum of OPW across active oarlocks (computed at the
// session level in OarlockManager). At the per-stroke layer, MFW2 doubling
// is applied to OPW; combinedPower aggregation lives upstream.
// ─────────────────────────────────────────────────────────────────────────────

import { catchFinishMetrics } from './algorithms/catchFinish';
import { maxForceMetrics } from './algorithms/forceMetrics';
import { slipWashMetrics } from './algorithms/slipwash';
import { oarlockPower, workPerStrokeJ } from './algorithms/power';

/**
 * Process one stroke for one oarlock and return all merged metrics.
 *
 * @param {object} args
 *   @param {number[]} args.forceN     — zero-corrected force samples (N)
 *   @param {number[]} args.angleDeg   — zero-corrected angle samples (deg)
 *   @param {number}   args.spm        — strokes per minute (cross-app input)
 *   @param {object}   args.rigging    — { ol, ib, sp, mfw2, ... }
 *   @param {string}   args.side       — 'port' | 'starboard' | 'sweep' | 'single_scull'
 *
 * @returns {object} merged metrics — ALL geometric angles unscaled; F/W/P
 *   doubled when rigging.mfw2 === true.
 */
export function processStroke({ forceN, angleDeg, spm, rigging = {}, side = 'sweep' } = {}) {
  const { CA, FA, EL } = catchFinishMetrics(forceN, angleDeg);
  const { MXF, MFA } = maxForceMetrics(forceN, angleDeg);
  const { SL, WA, theoretical_CA, theoretical_FA } = slipWashMetrics({
    actualCA: CA,
    actualFA: FA,
    rigging,
  });
  const workJ = workPerStrokeJ(forceN, angleDeg);
  const OPW = oarlockPower({ forceN, angleDeg, spm });

  // MFW2 BOUNDARY — only F / W / P get doubled. Geometric values are pristine.
  const mult = rigging?.mfw2 ? 2 : 1;
  const MXF_out = (MXF == null) ? null : MXF * mult;
  const work_out = (workJ == null) ? null : workJ * mult;
  const OPW_out = (OPW == null) ? null : OPW * mult;

  return {
    side,
    // Geometric / angular metrics — NEVER doubled
    CA,
    FA,
    EL,
    MFA,
    SL,
    WA,
    theoretical_CA,
    theoretical_FA,
    // Force / Work / Power — MFW2-adjusted
    MXF: MXF_out,
    work_j: work_out,
    OPW: OPW_out,
    // Bookkeeping
    spm: typeof spm === 'number' ? spm : null,
    mfw2_applied: rigging?.mfw2 === true,
  };
}

/**
 * Combine per-side stroke results into session-level totals.
 *  - combinedPower = sum(OPW) across active sides (each already MFW2-adjusted)
 *  - For sweep / single_scull rigs (one oarlock), combinedPower === that side's OPW.
 */
export function combinePerSide(perSideResults = []) {
  const valid = perSideResults.filter(r => r && typeof r.OPW === 'number');
  if (valid.length === 0) {
    return { combinedPower: null, sides: [] };
  }
  const combinedPower = valid.reduce((acc, r) => acc + r.OPW, 0);
  return { combinedPower, sides: valid.map(r => r.side) };
}
